import { parseThemeBlock, type ThemeBlock } from "../shared/presentation";
import { boundedKeys, exactKeys, GameSpecValidationError, glyph, integer, record, text } from "../shared/validators";

export interface EndlessRunnerSpec {
  readonly version: 1;
  readonly kind: "endless-runner";
  readonly title: string;
  readonly seed: number;
  readonly theme: ThemeBlock;
  readonly params: {
    readonly worldWidth: number;
    readonly worldHeight: number;
    readonly gravity: number;
    readonly jumpVelocity: number;
    readonly obstacleSpeed: number;
    readonly obstacleWidth: number;
    readonly minGapSeconds: number;
    readonly obstacleCount: number;
    /** Percent the obstacle speed grows to by the final obstacle (0-40). */
    readonly speedRampPercent?: number;
    readonly playerGlyph?: string;
    readonly obstacleGlyph?: string;
  };
}

/** Auto-run, tap to jump: endless runner, flappy-style, dino-run -- any brief about surviving a scrolling obstacle stream. */
export function parseEndlessRunnerSpec(root: Record<string, unknown>): EndlessRunnerSpec {
  exactKeys(root, ["version", "kind", "title", "seed", "theme", "params"], "game spec");
  if (root.kind !== "endless-runner") throw new GameSpecValidationError("unsupported game kind");

  const params = record(root.params, "params");
  boundedKeys(
    params,
    ["worldWidth", "worldHeight", "gravity", "jumpVelocity", "obstacleSpeed", "obstacleWidth", "minGapSeconds", "obstacleCount"],
    ["speedRampPercent", "playerGlyph", "obstacleGlyph"],
    "params",
  );
  const speedRampPercent = params.speedRampPercent === undefined ? undefined : integer(params.speedRampPercent, "params.speedRampPercent", 0, 40);
  const playerGlyph = glyph(params.playerGlyph, "params.playerGlyph");
  const obstacleGlyph = glyph(params.obstacleGlyph, "params.obstacleGlyph");

  return {
    version: 1,
    kind: "endless-runner",
    title: text(root.title, "title", 1, 80),
    seed: integer(root.seed, "seed", 0, 4_294_967_295),
    theme: parseThemeBlock(root.theme),
    params: {
      worldWidth: integer(params.worldWidth, "params.worldWidth", 480, 1_280),
      worldHeight: integer(params.worldHeight, "params.worldHeight", 320, 800),
      gravity: integer(params.gravity, "params.gravity", 900, 2_400),
      jumpVelocity: integer(params.jumpVelocity, "params.jumpVelocity", 400, 1_000),
      obstacleSpeed: integer(params.obstacleSpeed, "params.obstacleSpeed", 220, 600),
      obstacleWidth: integer(params.obstacleWidth, "params.obstacleWidth", 20, 60),
      minGapSeconds: integer(params.minGapSeconds, "params.minGapSeconds", 1, 4),
      obstacleCount: integer(params.obstacleCount, "params.obstacleCount", 10, 40),
      ...(speedRampPercent === undefined ? {} : { speedRampPercent }),
      ...(playerGlyph === undefined ? {} : { playerGlyph }),
      ...(obstacleGlyph === undefined ? {} : { obstacleGlyph }),
    },
  };
}
